import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Payment as PaymentIcon,
  Refresh as RefreshIcon,
  TableView as ExcelIcon,
} from '@mui/icons-material';
import {
  getMyConfig,
  initiatePayment,
  myDailyRevenue,
  myInvoices,
  type DailyRevenueRow,
  type InvoiceStatus,
  type SubscriptionInvoice,
} from '@/api/subscription';
import { useAuthStore } from '@/stores/authStore';
import { brand } from '@/theme/theme';
import { PaginationFooter } from '@/components';
import { downloadExcelFromBackend } from '@/utils/excelExport';
import { getLogoDataUrl } from '@/utils/logoDataUrl';

declare global {
  interface Window { Razorpay: any; } // eslint-disable-line @typescript-eslint/no-explicit-any
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function loadRazorpayScript(): Promise<boolean> {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

function statusChip(status: InvoiceStatus) {
  const map: Record<InvoiceStatus, { label: string; color: string }> = {
    PENDING:        { label: 'Pending',  color: '#e65100' },
    PAID:           { label: 'Paid',     color: '#2e7d32' },
    PARTIALLY_PAID: { label: 'Partial',  color: '#1565c0' },
    CANCELLED:      { label: 'Cancelled',color: '#757575' },
  };
  const m = map[status] ?? { label: status, color: '#757575' };
  return <Chip label={m.label} size="small" sx={{ bgcolor: m.color, color: '#fff', fontWeight: 700, fontSize: 11 }} />;
}

function fmt(n: number) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function periodLabel(year: number, month: number) {
  return new Date(year, month - 1).toLocaleString('default', { month: 'long', year: 'numeric' });
}

const RAZORPAY_KEY_ID = import.meta.env.VITE_RAZORPAY_KEY_ID ?? 'rzp_test_REPLACE_ME';

// ---------------------------------------------------------------------------
// PDF helpers — jsPDF (no html2canvas, no DOM rendering)
// ---------------------------------------------------------------------------

async function downloadGstInvoicePdf(
  inv: SubscriptionInvoice,
  gstRate: number,
  tenantName: string,
) {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  // jsPDF built-in fonts (Helvetica) are Latin-1 only — use "Rs." instead of ₹
  const r = (n: number) =>
    `Rs. ${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const fee    = Number(inv.invoiceAmount);
  const gst    = (inv.gstAmount   ?? 0) > 0 ? Number(inv.gstAmount)   : fee * gstRate;
  const total  = (inv.totalAmount ?? 0) > 0 ? Number(inv.totalAmount) : fee + gst;
  const paid   = Number(inv.paidAmount);
  const due    = total - paid;
  const cgst   = gst / 2;
  const gstPct = Math.round(gstRate * 100);
  const period = periodLabel(inv.periodYear, inv.periodMonth);
  const paidOn = inv.status === 'PAID' && inv.updatedAt ? fmtDate(inv.updatedAt) : '-';

  const logoDataUrl = await getLogoDataUrl().catch(() => null);
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = doc.internal.pageSize.getWidth(); // 210 mm
  const NAVY: [number,number,number] = [123, 97, 255];
  const GRAY: [number,number,number] = [107, 114, 128];

  // ── Header bar ──
  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, 30, 'F');
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', 5, 2, 56, 22);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Ardinotech', 10, 13);
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text('GSTIN: 36ABFCA0067L1ZM', 10, 28);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('GST Invoice', W - 10, 12, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(inv.invoiceNumber, W - 10, 20, { align: 'right' });

  // ── Tenant box ──
  doc.setFillColor(240, 244, 255);
  doc.roundedRect(10, 34, W - 20, 11, 2, 2, 'F');
  doc.setTextColor(30, 58, 138);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(tenantName, 14, 41.5);

  // ── Invoice meta ──
  autoTable(doc, {
    startY: 49,
    body: [
      ['Invoice Number', inv.invoiceNumber, 'Billing Period', period],
      ['Status',         inv.status,        'Paid On',        paidOn],
    ],
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: { top: 2.5, bottom: 2.5, left: 2, right: 4 }, textColor: [0,0,0] as [number,number,number] },
    columnStyles: {
      0: { textColor: GRAY, cellWidth: 38 },
      1: { fontStyle: 'bold', cellWidth: 60 },
      2: { textColor: GRAY, cellWidth: 28 },
      3: { fontStyle: 'bold' },
    },
  });

  // ── Line items ──
  // A4 content width ≈ 190 mm (10 mm margins each side)
  // Col widths: Desc=68, HSN=20, Taxable=30, CGST=24, SGST=24, Total=30 → 196 (auto-scaled by autotable)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const y1 = (doc as any).lastAutoTable.finalY + 5;
  autoTable(doc, {
    startY: y1,
    margin: { left: 10, right: 10 },
    head: [['Description', 'HSN/SAC', `Taxable (Rs.)`, `CGST ${gstPct/2}%`, `SGST ${gstPct/2}%`, 'Total (Rs.)']],
    body: [
      [
        { content: 'Platform Subscription Fee' },
        '998314',
        { content: r(fee),   styles: { halign: 'right' as const } },
        { content: r(cgst),  styles: { halign: 'right' as const } },
        { content: r(cgst),  styles: { halign: 'right' as const } },
        { content: r(total), styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
      ],
      [
        { content: 'Total', colSpan: 2, styles: { fontStyle: 'bold' as const } },
        { content: r(fee),   styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
        { content: r(cgst),  styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
        { content: r(cgst),  styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
        { content: r(total), styles: { halign: 'right' as const, fontStyle: 'bold' as const } },
      ],
    ],
    headStyles: {
      fillColor: [243, 244, 246] as [number,number,number],
      textColor: [0, 0, 0] as [number,number,number],
      fontSize: 10, fontStyle: 'bold',
    },
    bodyStyles: { fontSize: 10.5, textColor: [0,0,0] as [number,number,number] },
    alternateRowStyles: { fillColor: [255,255,255] as [number,number,number] },
    columnStyles: {
      0: { cellWidth: 68 },
      1: { cellWidth: 20 },
      2: { cellWidth: 30, halign: 'right' as const },
      3: { cellWidth: 24, halign: 'right' as const },
      4: { cellWidth: 24, halign: 'right' as const },
      5: { cellWidth: 30, halign: 'right' as const },
    },
  });

  // ── Summary ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const y2 = (doc as any).lastAutoTable.finalY + 6;
  autoTable(doc, {
    startY: y2,
    body: [
      ['Total Billed', r(total)],
      ['Amount Paid',  r(paid)],
      ['Balance Due',  r(due)],
    ],
    theme: 'plain',
    styles: { fontSize: 11.5, cellPadding: { top: 3, bottom: 3, left: 2, right: 2 }, textColor: [0,0,0] as [number,number,number] },
    columnStyles: {
      0: { textColor: GRAY, cellWidth: 40 },
      1: { fontStyle: 'bold', halign: 'right' as const, cellWidth: 50 },
    },
    tableWidth: 90,
    margin: { left: W - 100 },
  });

  // ── Footer ──
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const y3 = (doc as any).lastAutoTable.finalY + 8;
  doc.setDrawColor(229, 231, 235);
  doc.line(10, y3, W - 10, y3);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Computer-generated document. | Ardinotech B2B SaaS Platform | ${new Date().toLocaleString('en-IN')}`,
    W / 2, y3 + 5, { align: 'center' },
  );

  doc.save(`invoice-${inv.invoiceNumber}.pdf`);
}

async function downloadInvoiceListPdf(
  invoices: SubscriptionInvoice[],
  tenantName: string,
  gstRate: number,
) {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');
  const logoDataUrl = await getLogoDataUrl().catch(() => null);

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  const W = doc.internal.pageSize.getWidth();
  const NAVY: [number,number,number] = [123, 97, 255];
  const r = (n: number) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDisc = (inv: SubscriptionInvoice) =>
    inv.discountAmt != null && Number(inv.discountAmt) > 0
      ? `${r(Number(inv.discountAmt))} (${Number(inv.discountPct).toFixed(0)}%)`
      : '—';
  const fmtPaidOn = (inv: SubscriptionInvoice) =>
    inv.status === 'PAID' && inv.updatedAt
      ? new Date(inv.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';

  doc.setFillColor(...NAVY);
  doc.rect(0, 0, W, 30, 'F');
  if (logoDataUrl) {
    doc.addImage(logoDataUrl, 'PNG', 5, 2, 80, 26);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Ardinotech', 8, 14);
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.text('Subscription Invoice Report', W / 2, 12, { align: 'center' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(tenantName, W / 2, 20, { align: 'center' });
  doc.setFontSize(8);
  doc.text(`Generated: ${new Date().toLocaleString('en-IN')}`, W - 8, 13, { align: 'right' });

  const body = invoices.map((inv, i) => {
    const fee    = Number(inv.invoiceAmount);
    const netFee = inv.netPayable != null ? Number(inv.netPayable) : fee - (Number(inv.discountAmt) || 0);
    const gst    = (inv.gstAmount ?? 0) > 0 ? Number(inv.gstAmount) : netFee * gstRate;
    const total  = (inv.totalAmount ?? 0) > 0 ? Number(inv.totalAmount) : netFee + gst;
    const due    = total - Number(inv.paidAmount);
    return [
      String(i + 1),
      inv.invoiceNumber,
      periodLabel(inv.periodYear, inv.periodMonth),
      r(Number(inv.totalRevenue)),
      r(fee),
      fmtDisc(inv),
      r(netFee),
      r(gst),
      r(total),
      r(Number(inv.paidAmount)),
      r(due),
      inv.status,
      fmtPaidOn(inv),
    ];
  });

  const totFee   = invoices.reduce((s, i) => s + Number(i.invoiceAmount), 0);
  const totDisc  = invoices.reduce((s, i) => s + (Number(i.discountAmt) || 0), 0);
  const totNet   = totFee - totDisc;
  const totGst   = totNet * gstRate;
  const totTotal = totNet + totGst;
  const totPaid  = invoices.reduce((s, i) => s + Number(i.paidAmount), 0);
  body.push(['', '', 'TOTAL', '', r(totFee), totDisc > 0 ? r(totDisc) : '—', r(totNet), r(totGst), r(totTotal), r(totPaid), r(totTotal - totPaid), '', '']);

  autoTable(doc, {
    startY: 30,
    margin: { left: 5, right: 5 },
    head: [['#', 'Invoice #', 'Period', 'Revenue', 'Fee', 'Discount', 'Net Pay.', 'GST', 'Total', 'Paid', 'Due', 'Status', 'Paid On']],
    body,
    headStyles: { fillColor: NAVY, textColor: [255,255,255] as [number,number,number], fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 9, textColor: [0,0,0] as [number,number,number] },
    alternateRowStyles: { fillColor: [245,245,255] as [number,number,number] },
    columnStyles: {
      0:  { cellWidth: 6 },
      1:  { cellWidth: 24 },
      2:  { cellWidth: 24 },
      3:  { halign: 'right' as const, cellWidth: 22 },
      4:  { halign: 'right' as const, cellWidth: 20 },
      5:  { halign: 'right' as const, cellWidth: 26 },
      6:  { halign: 'right' as const, cellWidth: 20, fontStyle: 'bold' as const },
      7:  { halign: 'right' as const, cellWidth: 18 },
      8:  { halign: 'right' as const, cellWidth: 20, fontStyle: 'bold' as const },
      9:  { halign: 'right' as const, cellWidth: 18 },
      10: { halign: 'right' as const, cellWidth: 18 },
      11: { cellWidth: 18 },
      12: { cellWidth: 18 },
    },
  });

  const yFinal = (doc as any).lastAutoTable.finalY + 5; // eslint-disable-line @typescript-eslint/no-explicit-any
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(8);
  doc.text('Computer-generated report. | Ardinotech B2B SaaS Platform | GSTIN: 36ABFCA0067L1ZM', W / 2, yFinal, { align: 'center' });

  doc.save(`subscription-invoices-${tenantName.replace(/\s+/g, '_')}.pdf`);
}

async function downloadInvoiceListExcel(
  invoices: SubscriptionInvoice[],
  tenantName: string,
  gstRate: number,
) {
  const r = (n: number) => Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDisc = (inv: SubscriptionInvoice) =>
    inv.discountAmt != null && Number(inv.discountAmt) > 0
      ? `${r(Number(inv.discountAmt))} (${Number(inv.discountPct).toFixed(0)}%)`
      : '—';
  const fmtPaidOn = (inv: SubscriptionInvoice) =>
    inv.status === 'PAID' && inv.updatedAt
      ? new Date(inv.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : '—';

  const rows = invoices.map((inv, i) => {
    const fee    = Number(inv.invoiceAmount);
    const netFee = inv.netPayable != null ? Number(inv.netPayable) : fee - (Number(inv.discountAmt) || 0);
    const gst    = (inv.gstAmount ?? 0) > 0 ? Number(inv.gstAmount) : netFee * gstRate;
    const total  = (inv.totalAmount ?? 0) > 0 ? Number(inv.totalAmount) : netFee + gst;
    const due    = total - Number(inv.paidAmount);
    return [
      String(i + 1),
      inv.invoiceNumber,
      periodLabel(inv.periodYear, inv.periodMonth),
      r(Number(inv.totalRevenue)),
      r(fee),
      fmtDisc(inv),
      r(netFee),
      r(gst),
      r(total),
      r(Number(inv.paidAmount)),
      r(due),
      inv.status,
      fmtPaidOn(inv),
    ];
  });

  const totFee  = invoices.reduce((s, i) => s + Number(i.invoiceAmount), 0);
  const totDisc = invoices.reduce((s, i) => s + (Number(i.discountAmt) || 0), 0);
  const totNet  = totFee - totDisc;
  const totGst  = totNet * gstRate;
  const totTotal = totNet + totGst;
  const totPaid  = invoices.reduce((s, i) => s + Number(i.paidAmount), 0);
  rows.push(['', 'TOTAL', '', '', r(totFee), totDisc > 0 ? r(totDisc) : '—', r(totNet), r(totGst), r(totTotal), r(totPaid), r(totTotal - totPaid), '', '']);

  await downloadExcelFromBackend({
    title: `Subscription Invoices — ${tenantName}`,
    subTitle: `${tenantName}   |   Generated: ${new Date().toLocaleDateString('en-IN')}`,
    headers: ['#', 'Invoice #', 'Period', 'Revenue', 'Fee', 'Discount', 'Net Payable', 'GST', 'Total', 'Paid', 'Due', 'Status', 'Paid On'],
    rows,
  });
}

function downloadDailyRevenueExcel(
  rows: DailyRevenueRow[],
  inv: SubscriptionInvoice,
  tenantName: string,
) {
  const period      = periodLabel(inv.periodYear, inv.periodMonth);
  const totalRev    = rows.reduce((s, r) => s + Number(r.revenue), 0);
  const totalOrders = rows.reduce((s, r) => s + r.orderCount, 0);

  const dataRows = rows.map(r => {
    const day = new Date(r.date).toLocaleDateString('en-IN', { weekday: 'short' });
    return [r.date, day, String(r.orderCount), Number(r.revenue).toFixed(2)];
  });
  dataRows.push(['TOTAL', '', String(totalOrders), totalRev.toFixed(2)]);

  downloadExcelFromBackend({
    title: `Daily Revenue Report — ${tenantName} — ${period}`,
    subTitle: `Invoice: ${inv.invoiceNumber}  |  Period: ${period}`,
    headers: ['Date', 'Day', 'Orders', 'Revenue (Rs.)'],
    rows: dataRows,
  });
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function MySubscriptionPage() {
  const user = useAuthStore(s => s.user);
  const tenantName = user?.tenantName ?? 'My Business';

  const [gstRate, setGstRate] = useState(0.18);
  useEffect(() => {
    getMyConfig().then(c => setGstRate(Number(c.gstRate) / 100)).catch(() => {});
  }, []);

  const [statusFilter, setStatusFilter] = useState<InvoiceStatus | ''>('');
  const [page,       setPage]       = useState(0);
  const [invoices,   setInvoices]   = useState<SubscriptionInvoice[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [paying,          setPaying]          = useState<number | null>(null);
  const [downloadingRevId, setDownloadingRevId] = useState<number | null>(null);
  const pageSize = 20;

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await myInvoices({ status: statusFilter || undefined, page, size: pageSize });
      setInvoices(res.content);
      setTotalPages(res.totalPages);
      setTotalElements(res.totalElements);
    } catch (err: any) {
      setInvoices([]);
      setError(err?.message || 'Could not load invoices. Please try again.');
    }
    finally { setLoading(false); }
  }, [statusFilter, page]);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handlePay = async (inv: SubscriptionInvoice) => {
    setPaying(inv.id);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { alert('Could not load payment gateway. Check your internet connection.'); setPaying(null); return; }
      const order = await initiatePayment(inv.id);
      const gst   = (inv.gstAmount ?? 0) > 0 ? inv.gstAmount! : inv.invoiceAmount * gstRate;
      const total = (inv.totalAmount ?? 0) > 0 ? inv.totalAmount! : inv.invoiceAmount + gst;
      const due   = total - Number(inv.paidAmount);
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: 'Ardinotech Platform Fee',
        description: `Invoice ${order.invoiceNumber}`,
        order_id: order.razorpayOrderId,
        handler: async () => {
          alert('Payment successful! Invoice will be marked as Paid shortly.');
          await fetchInvoices();
        },
        prefill: { name: tenantName, email: '', contact: '' },
        theme: { color: brand.violet },
        modal: { ondismiss: () => setPaying(null) },
      };
      void due; // used in display, not here
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err: unknown) {
      const detail = (err as any)?.response?.data?.error ?? (err instanceof Error ? err.message : 'Payment initiation failed.');
      alert(`Payment Error: ${detail}`);
      setPaying(null);
    }
  };

  const handleDownloadRevenue = async (inv: SubscriptionInvoice) => {
    setDownloadingRevId(inv.id);
    try {
      const rows = await myDailyRevenue(inv.periodYear, inv.periodMonth);
      downloadDailyRevenueExcel(rows, inv, tenantName);
    } catch { /* ignore */ }
    finally { setDownloadingRevId(null); }
  };

  const canPay = (inv: SubscriptionInvoice) => inv.status === 'PENDING' || inv.status === 'PARTIALLY_PAID';

  return (
    <Box sx={{ p: 3, maxWidth: 1400 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 700, color: brand.navy, mb: 3 }}>
        Billing & Plan
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status"
            onChange={e => { setStatusFilter(e.target.value as InvoiceStatus | ''); setPage(0); }}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PENDING">Pending</MenuItem>
            <MenuItem value="PAID">Paid</MenuItem>
            <MenuItem value="PARTIALLY_PAID">Partially Paid</MenuItem>
          </Select>
        </FormControl>
        <IconButton size="small" onClick={fetchInvoices}><RefreshIcon fontSize="small" /></IconButton>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" startIcon={<ExcelIcon sx={{ fontSize: '14px !important', color: '#15803d' }} />}
            onClick={() => downloadInvoiceListExcel(invoices, tenantName, gstRate)}
            sx={{ fontSize: 11, color: '#15803d', borderColor: '#86efac', bgcolor: '#f0fdf4', textTransform: 'none', '&:hover': { bgcolor: '#dcfce7' } }}>
            Excel
          </Button>
          <Button size="small" variant="outlined" startIcon={<PdfIcon sx={{ fontSize: '14px !important', color: '#dc2626' }} />}
            onClick={() => downloadInvoiceListPdf(invoices, tenantName, gstRate)}
            sx={{ fontSize: 11, color: '#dc2626', borderColor: '#fca5a5', bgcolor: '#fff5f5', textTransform: 'none', '&:hover': { bgcolor: '#fee2e2' } }}>
            PDF
          </Button>
        </Box>
      </Box>

      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1100, '& .MuiTableCell-root': { px: 2.5, py: 1.5 } }}>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>Invoice #</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>Period</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }} align="right">Revenue</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }} align="right">Fee</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }} align="right">GST ({Math.round(gstRate * 100)}%)</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }} align="right">Total</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }} align="right">Paid</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }} align="right">Due</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap', color: '#2e7d32' }}>Paid On</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : invoices.length === 0 ? (
              <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: 13 }}>No invoices found</TableCell></TableRow>
            ) : (
              invoices.map(inv => {
                const gst   = (inv.gstAmount   ?? 0) > 0 ? inv.gstAmount!   : inv.invoiceAmount * gstRate;
                const total = (inv.totalAmount ?? 0) > 0 ? inv.totalAmount! : inv.invoiceAmount + gst;
                const due   = total - Number(inv.paidAmount);
                const isPaid = inv.status === 'PAID';
                return (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontSize: 12 }}>{inv.invoiceNumber}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{periodLabel(inv.periodYear, inv.periodMonth)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }} align="right">{fmt(inv.totalRevenue)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }} align="right">{fmt(inv.invoiceAmount)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }} align="right">{fmt(gst)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }} align="right">{fmt(total)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }} align="right">{fmt(inv.paidAmount)}</TableCell>
                    <TableCell sx={{ fontSize: 12, fontWeight: due > 0 ? 700 : 400, color: due > 0 ? '#c62828' : 'inherit' }} align="right">{fmt(due)}</TableCell>
                    <TableCell>{statusChip(inv.status)}</TableCell>
                    <TableCell sx={{ fontSize: 12, color: isPaid ? '#2e7d32' : '#9ca3af', fontWeight: isPaid ? 700 : 400 }}>
                      {isPaid && inv.updatedAt ? fmtDate(inv.updatedAt) : '—'}
                    </TableCell>
                    <TableCell>
                      {/* Layout: [Day Revenue] [Pay] [GST PDF] */}
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                        {/* Day Revenue — always visible, leftmost */}
                        <Tooltip title={`Download daily revenue for ${periodLabel(inv.periodYear, inv.periodMonth)}`}>
                          <Button size="small" variant="outlined"
                            startIcon={downloadingRevId === inv.id ? <CircularProgress size={11} /> : <ExcelIcon sx={{ fontSize: '11px !important', color: '#15803d' }} />}
                            disabled={downloadingRevId === inv.id}
                            onClick={() => handleDownloadRevenue(inv)}
                            sx={{ fontSize: 10, px: 1, py: 0.3, minWidth: 0, height: 24, color: '#15803d', borderColor: '#86efac', bgcolor: '#f0fdf4', textTransform: 'none', '&:hover': { bgcolor: '#dcfce7' } }}>
                            Day Revenue
                          </Button>
                        </Tooltip>
                        {/* Pay — center, only for PENDING/PARTIALLY_PAID */}
                        {canPay(inv) && (
                          <Button size="small" variant="contained"
                            startIcon={paying === inv.id ? <CircularProgress size={11} sx={{ color: '#fff' }} /> : <PaymentIcon sx={{ fontSize: '13px !important' }} />}
                            disabled={paying === inv.id}
                            onClick={() => handlePay(inv)}
                            sx={{ bgcolor: brand.violet, fontSize: 11, py: 0.4, px: 1.5, '&:hover': { bgcolor: brand.navy } }}>
                            Pay {fmt(due)}
                          </Button>
                        )}
                        {/* GST PDF — rightmost, only after any payment (PAID or PARTIALLY_PAID) */}
                        {(isPaid || inv.status === 'PARTIALLY_PAID') && (
                          <Tooltip title="Download GST Invoice PDF">
                            <Button size="small" variant="outlined"
                              startIcon={<PdfIcon sx={{ fontSize: '11px !important' }} />}
                              onClick={() => downloadGstInvoicePdf(inv, gstRate, tenantName)}
                              sx={{ fontSize: 10, px: 1, py: 0.3, minWidth: 0, height: 24, color: '#dc2626', borderColor: '#fca5a5', bgcolor: '#fff5f5', textTransform: 'none', '&:hover': { bgcolor: '#fee2e2' } }}>
                              GST PDF
                            </Button>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationFooter
        page={page}
        totalPages={totalPages}
        total={totalElements}
        rowsPerPage={pageSize}
        rowsPerPageOptions={[pageSize]}
        onPageChange={setPage}
        onRowsPerPageChange={() => {}}
        label="invoices"
      />
    </Box>
  );
}
